import {
  collection,
  getDocs,
  limit as firestoreLimit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';

const DEFAULT_ORGANIZATION_ID =
  process.env.REACT_APP_DEFAULT_ORGANIZATION_ID || 'default_organization';

export const getTelephonyOrganizationId = (user) =>
  user?.organizationId || DEFAULT_ORGANIZATION_ID;

export const canManageTelephony = (user) => (
  user?.isAdmin === true ||
  ['admin', 'super_admin', 'central_admin'].includes(user?.role)
);

export const canReceiveTelephonyCalls = (user) => (
  user?.isAdmin === true ||
  [
    'admin',
    'super_admin',
    'central_admin',
    'dispatcher',
    'central_dispatch',
    'operator_admin',
    'operator_staff'
  ].includes(user?.role)
);

const telephonyScopeConstraints = (user) => {
  const constraints = [
    where('organizationId', '==', getTelephonyOrganizationId(user))
  ];
  if (['operator_admin', 'operator_staff'].includes(user?.role) && user?.operatorId) {
    constraints.push(where('operatorId', '==', user.operatorId));
  }
  return constraints;
};

export const createTelephonyPairingCode = async (displayName) => {
  const callable = httpsCallable(functions, 'createTelephonyPairingCode');
  const result = await callable({ displayName: displayName?.trim() || '' });
  return result.data;
};

// Telefonanlagen, die das Portal anbinden kann. Der Typ steht am jeweiligen Anschluss
// (telephonyConnectors.provider); Altbestand ohne Feld sind FRITZ!Box-Connectoren.
// Muss zu TELEPHONY_PROVIDERS in functions/src/telephony/urlEvents.ts passen.
// Konzept: nextcab_telephony_connector/docs/KONZEPT_ANRUFERKENNUNG_WEITERE_TELEFONANLAGEN.md 5.1
export const TELEPHONY_SYSTEMS = {
  fritzbox: {
    label: 'FRITZ!Box',
    description: 'Ein kleines Programm auf einem Computer im selben Netzwerk liest die Anrufe der FRITZ!Box mit.',
    ingestClass: 'connector'
  },
  swyx_netphone: {
    label: 'Swyx / Telekom Octopus NetPhone',
    description: 'Der NetPhone Client ruft bei jedem Anruf eine Adresse auf. Keine Installation, nur ein Eintrag in der Skin.',
    ingestClass: 'url'
  },
  generic_url: {
    label: 'Andere Telefonanlage',
    description: 'Für Anlagen, die bei einem Anruf eine Web-Adresse aufrufen können (z. B. 3CX, Starface, Placetel, sipgate).',
    ingestClass: 'url'
  }
};

export const connectorSystemKey = (connector) =>
  (TELEPHONY_SYSTEMS[connector?.provider] ? connector.provider : 'fritzbox');

export const isUrlConnector = (connector) =>
  TELEPHONY_SYSTEMS[connectorSystemKey(connector)].ingestClass === 'url';

export const createTelephonyUrlEndpoint = async ({ provider, displayName, extension, reportsConnect }) => {
  const callable = httpsCallable(functions, 'createTelephonyUrlEndpoint');
  const result = await callable({
    provider,
    displayName: displayName?.trim() || '',
    extension: extension?.trim() || '',
    reportsConnect: reportsConnect === true
  });
  return result.data;
};

export const rotateTelephonyUrlEndpoint = async (connectorId) => {
  const callable = httpsCallable(functions, 'rotateTelephonyUrlEndpoint');
  const result = await callable({ connectorId });
  return result.data;
};

export const disableTelephonyConnector = async (connectorId) => {
  const callable = httpsCallable(functions, 'disableTelephonyConnector');
  const result = await callable({ connectorId });
  return result.data;
};

export const deleteTelephonyConnector = async (connectorId) => {
  const callable = httpsCallable(functions, 'deleteTelephonyConnector');
  const result = await callable({ connectorId });
  return result.data;
};

export const setTelephonyConnectorRole = async (connectorId, role) => {
  const callable = httpsCallable(functions, 'setTelephonyConnectorRole');
  const result = await callable({ connectorId, role });
  return result.data;
};

export const subscribeToTelephonyConnectors = (user, onData, onError) => {
  if (!user || !canReceiveTelephonyCalls(user)) return () => {};

  const connectorsQuery = query(
    collection(db, 'telephonyConnectors'),
    ...telephonyScopeConstraints(user)
  );

  return onSnapshot(connectorsQuery, (snapshot) => {
    const items = snapshot.docs
      .map((item) => ({ id: item.id, ...item.data() }))
      .sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0));
    onData(items.slice(0, 20));
  }, onError);
};

// Ein Anruf ist nur so lange ein Hinweis wert; danach zeigt ihn die Anrufliste.
export const ACTIVE_CALL_MAX_AGE_MS = 10 * 60 * 1000;
const ACTIVE_CALL_LIMIT = 20;

export const subscribeToIncomingCalls = (user, onData, onError) => {
  if (!user || !canReceiveTelephonyCalls(user)) return () => {};

  // Untergrenze beim Start des Abos: neuere Anrufe laufen weiter ein, die 30-Tage-Historie
  // bleibt draußen. orderBy + limit halten das Snapshot auch in langen Sitzungen klein
  // (Composite-Index organizationId [+ operatorId] + startedAt).
  const windowStart = Timestamp.fromMillis(Date.now() - ACTIVE_CALL_MAX_AGE_MS);

  const callsQuery = query(
    collection(db, 'incomingCallSessions'),
    ...telephonyScopeConstraints(user),
    where('startedAt', '>=', windowStart),
    orderBy('startedAt', 'desc'),
    firestoreLimit(ACTIVE_CALL_LIMIT)
  );

  return onSnapshot(callsQuery, (snapshot) => {
    onData(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
  }, onError);
};

export const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// --- Anrufliste (Berichte) ------------------------------------------------
// Anrufsessions werden 30 Tage aufbewahrt (Firestore-TTL auf expiresAt, siehe
// TELEPHONY_SESSION_RETENTION_MS in den Cloud Functions). Weiter zurück liegende
// Zeiträume liefern deshalb nichts mehr.
export const CALL_LOG_RETENTION_DAYS = 30;
const CALL_LOG_LIMIT = 2000;

export const CALL_SOURCE_LABELS = {
  fritzbox_callmonitor: 'Zentrale',
  swyx_netphone: 'Zentrale',
  pbx_url: 'Zentrale',
  ai_telephonist: 'KI-Telefonist'
};

const CALL_STATE_LABELS = {
  ringing: 'Klingelt',
  connected: 'Im Gespräch',
  ended: 'Beendet',
  completed: 'Beendet',
  missed: 'Verpasst'
};

const CALL_MATCH_LABELS = {
  matched: 'Kunde erkannt',
  ambiguous: 'Mehrere Treffer',
  unknown: 'Unbekannte Nummer',
  unavailable: 'Keine Rufnummer'
};

// answerStatus 'unknown': Die Anlage meldet die Annahme nicht (bei Minicar Kassel belegt
// taxi.de dieses Ereignis). Dann nicht „Verpasst" behaupten.
export const callStateLabel = (state, answerStatus) => {
  if (state === 'ended' && answerStatus === 'unknown') return 'Beendet (Annahme unbekannt)';
  return CALL_STATE_LABELS[state] || state || 'Unbekannt';
};
export const callMatchLabel = (status) => CALL_MATCH_LABELS[status] || '';
export const callSourceLabel = (source) => CALL_SOURCE_LABELS[source] || 'Zentrale';

export const formatCallDuration = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return '-';
  const total = Math.max(0, Math.round(Number(seconds)));
  const minutes = Math.floor(total / 60);
  return `${minutes}:${String(total % 60).padStart(2, '0')} min`;
};

// Rohdokument in eine flache Zeile für Tabelle, Detailansicht und CSV-Export bringen.
export const normalizeCallSession = (session) => {
  const isAiCall = session.source === 'ai_telephonist';
  const isWithheld = session.numberPresentation === 'withheld' ||
    (!session.callerNumberE164 && !session.callerNumberRaw);

  return {
    id: session.id,
    startedAt: toDate(session.startedAt) || toDate(session.createdAt),
    connectedAt: toDate(session.connectedAt),
    endedAt: toDate(session.endedAt),
    source: session.source || 'fritzbox_callmonitor',
    sourceLabel: callSourceLabel(session.source),
    isAiCall,
    state: session.state || '',
    stateLabel: callStateLabel(session.state, session.answerStatus),
    isMissed: session.state === 'missed',
    callerNumber: session.callerNumberE164 || session.callerNumberRaw || '',
    isWithheld,
    calledNumber: session.calledNumberE164 || session.calledNumberRaw || '',
    line: session.line || '',
    customerId: session.matchedCustomer?.id || session.matchedCustomerIds?.[0] || '',
    customerName: session.matchedCustomer?.name || '',
    customerAddress: session.matchedCustomer?.address || '',
    matchStatus: session.matchStatus || '',
    matchLabel: callMatchLabel(session.matchStatus),
    durationSeconds: typeof session.durationSeconds === 'number' ? session.durationSeconds : null,
    rideId: session.rideId || '',
    summary: session.summary || '',
    transcript: session.transcript || '',
    conversationLink: session.conversationLink || ''
  };
};

// Anrufe eines Zeitraums laden. organizationId muss im where stehen (die Firestore-Rules
// erlauben nur den eigenen Mandanten), deshalb braucht die Abfrage einen Composite-Index
// organizationId + startedAt (bzw. + operatorId für Unternehmer-Konten).
export const fetchIncomingCalls = async (user, { startDate, endDate }) => {
  if (!user || !canReceiveTelephonyCalls(user)) return [];

  const snapshot = await getDocs(query(
    collection(db, 'incomingCallSessions'),
    ...telephonyScopeConstraints(user),
    where('startedAt', '>=', Timestamp.fromDate(startDate)),
    where('startedAt', '<=', Timestamp.fromDate(endDate)),
    orderBy('startedAt', 'desc'),
    firestoreLimit(CALL_LOG_LIMIT)
  ));

  return snapshot.docs.map((docSnap) => normalizeCallSession({ id: docSnap.id, ...docSnap.data() }));
};
